import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { translate } from 'shared/helpers/utils';
import styles from '../Groups.module.scss';
import { SecondaryNav, SecondaryNavItem } from '@digicert/dcone-common-ui';
import { useLocation, useHistory } from 'react-router-dom';
import { RouterConfig } from '../../../core/utils/constants';
import GroupOverview from '../group-details/group-overview/GroupOverview';
import GroupAccess from '../group-details/group-access/GroupAccess';
import { useDispatch } from 'react-redux';
import { fetchGroupDetails } from '../Groups.actions';
import { Source } from '../Groups.types';

enum Sections {
  Overview = 'overview',
  GroupAccess = 'group-access'
}

interface GroupDetailsProps {
  match: {
    params: {
      id: string;
    };
  };
}

export const GroupDetails = (props: GroupDetailsProps): ReactElement => {
  const { match } = props;
  const [section, setSection] = useState(Sections.Overview);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [groupName, setGroupName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [groupData, setGroupData] = useState<any>(null);

  const loadGroupDetails = async () => {
    try {
      setLoading(true);
      const response: any = await dispatch(fetchGroupDetails(match.params.id));
      if (response?.name) {
        setGroupData(response);
        const displayName = response?.source === Source.PLATFORM
          ? (translate(`groups.${response.name}`) as string || response.name)
          : response.name;
        setGroupName(displayName);
      }
    } catch (error) {
      console.error('Failed to fetch group details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroupDetails();
  }, [match.params.id, dispatch]);

  // Keep section state in sync with current route
  useEffect(() => {
    const isGroupAccess = location.pathname === RouterConfig.groups.groupAccess(match.params.id);
    setSection(isGroupAccess ? Sections.GroupAccess : Sections.Overview);
  }, [location.pathname, match.params.id]);

  const navItems: SecondaryNavItem[] = useMemo(() => {
    const subItems = [
      {
        id: Sections.Overview,
        label: translate('accounts.details.scim.groups.overview') as string,
        url: '#',
        onClick: (e) => {
          e.preventDefault();
          history.push(RouterConfig.groups.details(match.params.id));
          setSection(Sections.Overview);
        },
        active: location.pathname === RouterConfig.groups.details(match.params.id),
        externalLink: false,
        disabled: false,
      }
    ];

    if (groupData?.source !== Source.PLATFORM) {
      subItems.push({
        id: Sections.GroupAccess,
        label: translate('accounts.details.scim.groups.groupAccess') as string,
        url: '#',
        onClick: (e) => {
          e.preventDefault();
          history.push(RouterConfig.groups.groupAccess(match.params.id));
          setSection(Sections.GroupAccess);
        },
        active: location.pathname.startsWith(RouterConfig.groups.groupAccess(match.params.id)),
        externalLink: false,
        disabled: false
      });
    }

    return [
      {
        id: `secondary-nav-`,
        label: groupName,
        collapsed: false,
        subItems
      }
    ];
  }, [groupName, match.params.id, location.pathname, groupData?.source]);

  const renderContent = () => {
    switch (section) {
      case Sections.Overview:
        return <GroupOverview groupId={match.params.id} groupData={groupData} loading={loading} />;
      case Sections.GroupAccess:
        return <GroupAccess groupId={match.params.id} groupData={groupData} loading={loading} onRefresh={loadGroupDetails} />;
      default:
        return <GroupOverview groupId={match.params.id} groupData={groupData} loading={loading} />;
    }
  };

  return (
    <div className={styles.groups}>
      <div className={styles.nav}>
        <SecondaryNav key={`${match.params.id}-${groupName || 'loading'}-${location.pathname}`} items={navItems} />
      </div>
      <div className={styles.content}>
        {renderContent()}
      </div>
    </div>
  );
};

export default GroupDetails;
